// Client for this app's own backend (server/src/index.js's /session/*
// routes) — same-origin in every real deployment, since the frontend and
// this small backend are always served together as one project. Unlike
// cloudish-admin, this app never talks to the control plane's /api/v1/*
// directly: every account action (claim, balance, regenerate, top-up) is
// mediated server-side, authenticated by the caller's own Auth0 access
// token on every request rather than a credential the browser holds.

let tokenProvider: () => Promise<string | null> = async () => null;

export function setTokenProvider(provider: () => Promise<string | null>) {
  tokenProvider = provider;
}

// Called on any 401 from this app's backend — i.e. the Auth0 session it
// thought it had is no longer accepted (see auth.tsx's expireSession).
let onUnauthorized: () => void = () => {};

export function setUnauthorizedHandler(handler: () => void) {
  onUnauthorized = handler;
}

export class ApiError extends Error {
  status: number;
  payload: unknown;

  constructor(message: string, status: number, payload?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.payload = payload;
  }
}

async function authHeaders(): Promise<Record<string, string>> {
  const token = await tokenProvider();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    ...(options.body ? { 'content-type': 'application/json' } : {}),
    ...(await authHeaders()),
    ...((options.headers as Record<string, string>) || {}),
  };
  const response = await fetch(path, { ...options, headers });
  const text = await response.text();
  let payload: unknown = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    payload = text;
  }
  if (response.status === 401) onUnauthorized();
  if (!response.ok) {
    const message = (payload as { error?: string })?.error || (typeof payload === 'string' ? payload : response.statusText);
    throw new ApiError(message || 'request failed', response.status, payload);
  }
  return payload as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),
};

export default api;
